import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { msalConfig } from './authConfig';
import { ApiProvider } from './services/api/provider';
import { TokenManager } from './auth/tokenManager';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ABTestProvider } from './contexts/ABTestContext';
import { LicenseInfo } from '@mui/x-license';

// Configure MUI X License Key
if (process.env.REACT_APP_MUI_X_LICENSE_KEY) {
  LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_X_LICENSE_KEY);
} else {
  console.warn(
    '⚠️ MUI X license key not found. Premium features may display watermarks.'
  );
}

// Suppress MUI DataGrid skeleton cell error globally
const originalError = console.error;
console.error = (...args: unknown[]) => {
  const message = String(args[0] || '');
  if (message.includes('Expected skeleton cells to be defined')) {
    return; // Suppress this specific MUI X error
  }
  originalError.apply(console, args);
};

// Also suppress as window error event
window.addEventListener('error', event => {
  if (
    event.message &&
    event.message.includes('Expected skeleton cells to be defined')
  ) {
    event.preventDefault();
    event.stopPropagation();
    return false;
  }
});

const msalInstance = new PublicClientApplication(msalConfig);

async function bootstrap() {
  const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement
  );

  try {
    console.log('🔎 Initializing MSAL...');
    await msalInstance.initialize();

    console.log('🔄 Handling redirect promise...');
    // Set a global lock while handling redirect to prevent token requests
    const redirectHandling = (async () => {
      await msalInstance.handleRedirectPromise().catch(err => {
        console.error('❌ Redirect handling error:', err);
      });
    })();
    TokenManager.setGlobalLock(redirectHandling);
    await redirectHandling;
    console.log('✅ Redirect handled');

    root.render(
      <React.StrictMode>
        <MsalProvider instance={msalInstance}>
          <ErrorBoundary>
            <ApiProvider baseURL={process.env.REACT_APP_API_BASE || '/api'}>
              <ABTestProvider>
                <App />
              </ABTestProvider>
            </ApiProvider>
          </ErrorBoundary>
        </MsalProvider>
      </React.StrictMode>
    );
  } catch (err) {
    console.error('❌ MSAL bootstrap failed:', err);
    root.render(
      <div style={styles.containerStyle}>
        <p style={styles.errorTextStyle}>
          Failed to initialize authentication. Please try again or contact
          support.
        </p>
      </div>
    );
  }
}

const styles: Record<string, React.CSSProperties> = {
  containerStyle: {
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorTextStyle: {
    color: '#dc2626', // text-red-600
  },
};

bootstrap();
